import { defineStore } from "pinia";
import api from "@/utils/axios";
import { ref, computed } from "vue";
import { useRouter } from "vue-router";

export const useAuthStore = defineStore("auth", () => {
    const user = ref<any>(null);
    const token = ref<string | null>(localStorage.getItem('auth_token'));
    const router = useRouter();

    async function login(email: string, password: string) {
        try {
            const response = await api.post('/auth/login', { email, password });

            if (response.data.status === 'success') {
                const data = response.data.data;
                token.value = data.token;
                user.value = data.user;

                localStorage.setItem('auth_token', data.token);
                // Aquí se podría guardar user info también si se desea persistencia simple
                localStorage.setItem('user_info', JSON.stringify(data.user));

                const lang = data.user.settings?.lang;
                if (lang === 'es' || lang === 'en') {
                    api.defaults.headers.common['Accept-Language'] = lang;
                    const { default: i18n } = await import("@/utils/i18n");
                    if (i18n.global.locale.value !== lang) {
                        i18n.global.locale.value = lang;
                    }
                }

                return true;
            }
            return false;
        } catch (error) {
            console.error('Login Error:', error);
            throw error;
        }
    }

    function logout() {
        token.value = null;
        user.value = null;
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_info');
        window.location.href = '/login';
    }

    // Inicializar estado desde localStorage si existe
    if (localStorage.getItem('user_info')) {
        try {
            user.value = JSON.parse(localStorage.getItem('user_info')!);
            // Always refresh profile from backend to sync role
            refreshProfile();
        } catch (e) {
            localStorage.removeItem('user_info');
        }
    }

    async function refreshProfile() {
        if (!token.value) return;
        try {
            // Need an endpoint that returns "me". UsersController uses index/show but restricted.
            // Let's assume GET /users/me or GET /auth/me exists?
            // AuthController usually has 'me'. Let's check AuthController.php. 
            // If not, we can use GET /auth/me if routes exist.
            // For now, let's try GET /auth/me.
            const response = await api.get('/auth/me');
            if (response.data.status === 'success') {
                const refreshedUser = response.data.data;
                user.value = refreshedUser;
                localStorage.setItem('user_info', JSON.stringify(refreshedUser));
                // Update i18n if needed
                const lang = refreshedUser.settings?.lang;
                if (lang === 'es' || lang === 'en') {
                    const { default: i18n } = await import("@/utils/i18n");
                    if (i18n.global.locale.value !== lang) {
                        i18n.global.locale.value = lang;
                    }
                }
            }
        } catch (e) {
            console.error("Failed to refresh profile", e);
            // Optional: if 401, logout?
        }
    }

    const isAuthenticated = computed(() => !!token.value);

    return {
        user,
        token,
        isAuthenticated,
        login,
        logout
    };
});
